import React, { ReactElement, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { PredkladatelInput } from 'slovlex-idsk-react-components';
import PredkladatelUpdateButtons from './component/PredkladatelUpdateButtons';
import { useSuccessMessageContext } from '../../../context/SuccessMessageContext';
import routes from '../../../routing/routes';
import { SafeParameterSetter } from '../../../types/SafeParameterSetter';
import useFieldErrorMessages from '../../useFieldErrorMessages';
import { useClient } from '../../../context/ClientStatusContext';
import { updatePredkladatel } from '../../../client/legislativnyMaterial/predkladatelUpdateClient';
import Predkladatel from '../../../model/legislativnyMaterial/Predkladatel';
import './predkladatelUpdate.scss';
import LegislativnyMaterialDetail from '../../../model/legislativnyMaterial/LegislativnyMaterialDetail';
import { UserRoleEnum, userRoleId } from '../../../model/currentuser/UserRole';
import useSearchOrganizacie from '../../../hooks/useSearchOrganizacie';
import {
  MAX_MULTISELECT_RESULT,
  SHOW_ALL_MAX_RESULT,
} from '../../../constants/MultiSelectConstant';
import useOpravnenyPouzivatelList from '../../../hooks/useOpravnenyPouzivatelList';
import { pouzivatelIsAdmin } from '../../../function/permissions';
import { useUserContext } from '../../../context/UserContext';
import { SubjectItem } from '../../../model/legislativnyMaterial/SubjectItem';

interface Props {
  materialDetail: LegislativnyMaterialDetail,
}

const PredkladatelUpdate = ({
  materialDetail,
}: Props): ReactElement => {
  const navigate = useNavigate();
  const { pouzivatel } = useUserContext();
  const {
    type,
    year,
    index,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number }>();
  const { displaySuccess } = useSuccessMessageContext();
  const [organizaciaSearch, setOrganizaciaSearch] = useState<string>('');
  const [limit, setLimit] = useState(MAX_MULTISELECT_RESULT);
  const initialPredkladatel = {
    organizacia: materialDetail.aktualnaPredkladatelskaInstitucia,
    predkladatel: materialDetail.aktualnyPredkladatel,
  };
  const [updatedPredkladatel, setUpdatedPredkladatel] = useState<Predkladatel>(initialPredkladatel);
  const [reloadPredkladatel, setReloadPredkladatel] = useState(true);
  const [predkladatelMap, setPredkladatelMap] = useState(new Map());

  const {
    organizacie: organizaciaList,
    organizacieStatus: organizaciaListStatus,
  } = useSearchOrganizacie({
    typ: undefined,
    ids: [],
    ldap: true,
    legislativna: true,
    platna: true,
    nazov: updatedPredkladatel?.organizacia?.nazov || organizaciaSearch,
    limit,
  });

  const {
    pouzivatelia: predkladatelList,
    pouzivateliaStatus: predkladatelListStatus,
  } = useOpravnenyPouzivatelList(
    reloadPredkladatel,
    setReloadPredkladatel,
    materialDetail?.typ?.legislativny ? userRoleId(UserRoleEnum.PREDKLADATEL_LEG)
      : userRoleId(UserRoleEnum.PREDKLADATEL_NELEG),
    updatedPredkladatel?.organizacia?.ldapUuid,
  );

  const {
    wrapClient: updatePredkladatelClient,
    status: updatePredkladatelStatus,
  } = useClient(updatePredkladatel);
  const {
    getFieldErrorMessage,
    clearFieldErrorMessage,
  } = useFieldErrorMessages();

  useEffect(() => {
    if (!predkladatelMap?.has(updatedPredkladatel?.organizacia?.uuid)) {
      setReloadPredkladatel(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedPredkladatel?.organizacia?.uuid]);

  useEffect(() => {
    if (predkladatelList && predkladatelList.length !== 0
      && !predkladatelMap?.has(updatedPredkladatel?.organizacia?.uuid)) {
      const updatedMap = predkladatelMap;
      updatedMap.set(updatedPredkladatel?.organizacia?.uuid, predkladatelList);
      setPredkladatelMap(updatedMap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predkladatelList]);

  const updateField: SafeParameterSetter<Predkladatel> = (fieldName, value) => {
    setUpdatedPredkladatel(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
    clearFieldErrorMessage(fieldName);
  };

  const handlePredkladatelUpdate = async (): Promise<void> => {
    await updatePredkladatelClient(type, year, index, updatedPredkladatel);
    navigate(routes.procesy.detail.withParams(type, year, index));
    displaySuccess('Vaša požiadavka bola úspešne vykonaná.');
  };

  function setPredkladatel(organizacia: SubjectItem, predkladatel: SubjectItem): SubjectItem {
    if (predkladatelMap?.has(organizacia?.uuid) && predkladatelMap?.get(organizacia?.uuid)
      .map(x => x?.uuid)
      .includes(predkladatel?.uuid)) {
      return predkladatel;
    }
    return null;
  }

  return (
    <div className="predkladatel-update">
      <>
        {organizaciaList && (
          <PredkladatelInput
            loadingOrganizacia={organizaciaListStatus.loading}
            initialValues={[initialPredkladatel]}
            handleShowAll={() => {
              setLimit(SHOW_ALL_MAX_RESULT);
            }}
            withShowAllButton={(limit === MAX_MULTISELECT_RESULT
                && organizaciaList.length === MAX_MULTISELECT_RESULT)
              || (limit === SHOW_ALL_MAX_RESULT && organizaciaList.length <= MAX_MULTISELECT_RESULT)}
            searchText={organizaciaSearch}
            setSearchText={(text) => {
              setOrganizaciaSearch(text);
              setLimit(MAX_MULTISELECT_RESULT);
            }}
            className="predkladatel-input__placeholder"
            label="Nový predkladateľ"
            error={getFieldErrorMessage('aktualnyPredkladatel')
            || getFieldErrorMessage('aktualnaPredkladatelskaInstitucia')}
            required
            loadingPredkladatel={predkladatelListStatus.loading}
            organizaciaData={organizaciaList}
            predkladatelData={predkladatelMap}
            selectedItemList={[updatedPredkladatel]}
            setSelectedItemList={((itemList) => {
              updateField('organizacia', itemList[0].organizacia);
              updateField('predkladatel', setPredkladatel(itemList[0].organizacia, itemList[0].predkladatel));
            })}
            width="full"
            multiSelect={false}
            organizationSelectionDisabled={!pouzivatelIsAdmin(pouzivatel)}
            predkladatelLabel="Vyberte nového predkladateľa"
          />
        )}
      </>
      <PredkladatelUpdateButtons
        onClick={handlePredkladatelUpdate}
        loading={updatePredkladatelStatus.loading}
        isAdmin={pouzivatelIsAdmin(pouzivatel)}
      />
    </div>
  );
};

export default PredkladatelUpdate;
